// src/pages/admin/bookings/AdminBookingsCalendarPage.tsx

import * as React from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Stack,
    Typography,
    Tooltip,
    Card,
    CardContent,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";

import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import BookingStatusChip from "./components/BookingStatusChip";
import { BookingListItem, PagedResponse, toISODateInput, safeStr } from "./bookingTypes";

import { adminFetch } from "../../../services/adminFetch";

function startOfMonth(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function addMonths(d: Date, delta: number) {
    return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}
function isoDate(d: Date) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}
function dayKey(d: Date) {
    return isoDate(d);
}
function rangeDays(gridStart: Date, gridEnd: Date) {
    const out: Date[] = [];
    const cur = new Date(gridStart);
    while (cur.getTime() <= gridEnd.getTime()) {
        out.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return out;
}
function clampToDate(s?: string | null): string {
    return toISODateInput(s) || "";
}
function overlapsDay(b: BookingListItem, dayISO: string) {
    const a = clampToDate(b.check_in);
    const c = clampToDate(b.check_out);
    if (!a || !c) return false;
    // day is within [check_in, check_out) (checkout exclusive)
    return a <= dayISO && dayISO < c;
}
function dowShort(d: Date) {
    return d.toLocaleDateString(undefined, { weekday: "short" });
}
function monthTitle(d: Date) {
    return d.toLocaleString(undefined, { month: "long", year: "numeric" });
}

export default function AdminBookingsCalendarPage() {
    const { locale } = useParams<{ locale: string }>();
    const navigate = useNavigate();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [cursorMonth, setCursorMonth] = React.useState<Date>(() => startOfMonth(new Date()));
    const [loading, setLoading] = React.useState(false);
    const [err, setErr] = React.useState<string | null>(null);
    const [items, setItems] = React.useState<BookingListItem[]>([]);

    const monthStart = React.useMemo(() => startOfMonth(cursorMonth), [cursorMonth]);
    const monthEnd = React.useMemo(() => endOfMonth(cursorMonth), [cursorMonth]);

    // build a calendar grid Monday-start
    const gridStart = React.useMemo(() => {
        const d = new Date(monthStart);
        const day = (d.getDay() + 6) % 7; // Sun(0)=>6, Mon(1)=>0
        d.setDate(d.getDate() - day);
        return d;
    }, [monthStart]);

    const gridEnd = React.useMemo(() => {
        const d = new Date(monthEnd);
        const day = (d.getDay() + 6) % 7;
        d.setDate(d.getDate() + (6 - day));
        return d;
    }, [monthEnd]);

    const days = React.useMemo(() => rangeDays(gridStart, gridEnd), [gridStart, gridEnd]);

    const fetchMonth = React.useCallback(async () => {
        setLoading(true);
        setErr(null);

        const from = isoDate(monthStart);
        const to = isoDate(new Date(monthEnd.getFullYear(), monthEnd.getMonth(), monthEnd.getDate() + 1));

        const p = new URLSearchParams();
        p.set("from", from);
        p.set("to", to);
        p.set("limit", "500");
        p.set("offset", "0");

        const url = `/api/admin/bookings?${p.toString()}`;

        try {
            const res = await adminFetch(url, { method: "GET" });

            const txt = await res.text();
            let j: any = {};
            try {
                j = txt ? JSON.parse(txt) : {};
            } catch {
                j = { _raw: txt };
            }

            if (!res.ok) throw new Error(j?.error || `Request failed (${res.status})`);

            const data = j as PagedResponse<BookingListItem>;
            setItems(Array.isArray(data.items) ? data.items : []);
        } catch (e: any) {
            setErr(e?.message || "Failed to load calendar bookings.");
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [monthStart, monthEnd]);

    React.useEffect(() => {
        fetchMonth();
    }, [fetchMonth]);

    const title = React.useMemo(() => monthTitle(monthStart), [monthStart]);

    // index bookings by day for quick render
    const byDay = React.useMemo(() => {
        const map = new Map<string, BookingListItem[]>();
        for (const d of days) map.set(dayKey(d), []);

        for (const b of items) {
            for (const d of days) {
                const k = dayKey(d);
                if (overlapsDay(b, k)) {
                    const arr = map.get(k);
                    if (arr) arr.push(b);
                }
            }
        }

        for (const [k, arr] of map.entries()) {
            arr.sort((a, b) => safeStr(a.status).localeCompare(safeStr(b.status)));
            map.set(k, arr);
        }

        return map;
    }, [items, days]);

    // Mobile agenda: only show days that are in current month (clean)
    const monthDays = React.useMemo(
        () => days.filter((d) => d.getMonth() === monthStart.getMonth()),
        [days, monthStart],
    );

    return (
        <Stack spacing={2}>
            <Paper
                elevation={0}
                variant="outlined"
                sx={{
                    borderRadius: 3,
                    borderColor: "divider",
                    overflow: "hidden",
                    background:
                        "radial-gradient(900px 500px at 15% -20%, rgba(25,118,210,0.10), transparent 60%), radial-gradient(900px 500px at 85% -10%, rgba(156,39,176,0.10), transparent 60%)",
                }}
            >
                {/* Header */}
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    alignItems={{ md: "center" }}
                    sx={{ p: 2, gap: 1.5 }}
                >
                    <Stack spacing={0.25}>
                        <Typography variant="h6" sx={{ fontWeight: 950 }}>
                            Bookings calendar
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {isMobile ? "Agenda view on mobile." : "Month view (no external library)."}
                        </Typography>
                    </Stack>

                    <Box sx={{ flex: 1 }} />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center" sx={{ width: { xs: "100%", sm: "auto" } }}>
                        <Button
                            fullWidth={isMobile}
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            component={RouterLink}
                            to={`/${locale || "el"}/admin/bookings`}
                            sx={{ borderRadius: 2, fontWeight: 900, whiteSpace: "nowrap" }}
                        >
                            Back
                        </Button>

                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchMonth} disabled={loading} sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                <Divider />

                <Box sx={{ p: 2 }}>
                    {err ? <Alert severity="error">{err}</Alert> : null}

                    {/* Month navigation */}
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        alignItems={{ sm: "center" }}
                        justifyContent="space-between"
                        gap={1}
                        sx={{ mb: 1.5 }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <IconButton onClick={() => setCursorMonth((d) => addMonths(d, -1))} disabled={loading}>
                                <ChevronLeftIcon />
                            </IconButton>

                            <Typography variant="h6" sx={{ fontWeight: 950 }}>
                                {title}
                            </Typography>

                            <IconButton onClick={() => setCursorMonth((d) => addMonths(d, 1))} disabled={loading}>
                                <ChevronRightIcon />
                            </IconButton>
                        </Stack>

                        <Chip
                            variant="outlined"
                            label={loading ? "Loading…" : `${items.length} bookings`}
                            sx={{ borderRadius: 999, fontWeight: 900, alignSelf: { xs: "flex-start", sm: "center" } }}
                        />
                    </Stack>

                    {/* MOBILE: Agenda */}
                    {isMobile ? (
                        <Stack spacing={1.5}>
                            {monthDays.map((d) => {
                                const k = dayKey(d);
                                const dayBookingsAll = byDay.get(k) || [];

                                return (
                                    <Card key={k} variant="outlined" sx={{ borderRadius: 3 }}>
                                        <CardContent sx={{ p: 1.5 }}>
                                            <Stack spacing={1}>
                                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                    <Typography sx={{ fontWeight: 950 }}>
                                                        {dowShort(d)} · {k}
                                                    </Typography>
                                                    {loading ? <CircularProgress size={14} /> : null}
                                                </Stack>

                                                {dayBookingsAll.length === 0 ? (
                                                    <Typography variant="body2" color="text.secondary">
                                                        —
                                                    </Typography>
                                                ) : (
                                                    <Stack spacing={1}>
                                                        {dayBookingsAll.map((b) => {
                                                            const title = safeStr(
                                                                b.property_title,
                                                                b.property_id ? `Property #${b.property_id}` : "—",
                                                            );
                                                            return (
                                                                <Box
                                                                    key={b.id}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onClick={() => navigate(`/${locale || "el"}/admin/bookings/${b.id}`)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter") navigate(`/${locale || "el"}/admin/bookings/${b.id}`);
                                                                    }}
                                                                    sx={{
                                                                        cursor: "pointer",
                                                                        borderRadius: 2,
                                                                        border: "1px solid",
                                                                        borderColor: "divider",
                                                                        p: 1,
                                                                        "&:hover": { backgroundColor: "action.hover" },
                                                                    }}
                                                                >
                                                                    <Stack spacing={0.5}>
                                                                        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                                                                            <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                                                                #{b.id}
                                                                            </Typography>
                                                                            <BookingStatusChip status={b.status ?? null} size="small" variant="outlined" />
                                                                        </Stack>

                                                                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                                                            {title}
                                                                        </Typography>

                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {clampToDate(b.check_in) || "—"} → {clampToDate(b.check_out) || "—"}
                                                                        </Typography>
                                                                    </Stack>
                                                                </Box>
                                                            );
                                                        })}
                                                    </Stack>
                                                )}
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                );
                            })}

                            {!monthDays.length ? (
                                <Typography variant="body2" color="text.secondary">
                                    No days to render.
                                </Typography>
                            ) : null}
                        </Stack>
                    ) : (
                        /* DESKTOP: Grid */
                        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
                            {/* Weekday header */}
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(7, 1fr)",
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                    backgroundColor: "background.default",
                                }}
                            >
                                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((w) => (
                                    <Box key={w} sx={{ p: 1, textAlign: "center" }}>
                                        <Typography variant="caption" sx={{ fontWeight: 900, letterSpacing: 0.6 }} color="text.secondary">
                                            {w}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            {/* Grid */}
                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
                                {days.map((d) => {
                                    const k = dayKey(d);
                                    const inMonth = d.getMonth() === monthStart.getMonth();
                                    const dayNum = d.getDate();
                                    const dayBookingsAll = byDay.get(k) || [];
                                    const dayBookings = dayBookingsAll.slice(0, 4);
                                    const hasMore = dayBookingsAll.length > 4;

                                    return (
                                        <Box
                                            key={k}
                                            sx={{
                                                minHeight: 120,
                                                borderRight: "1px solid",
                                                borderBottom: "1px solid",
                                                borderColor: "divider",
                                                p: 1,
                                                backgroundColor: inMonth ? "background.paper" : "action.hover",
                                                opacity: inMonth ? 1 : 0.7,
                                            }}
                                        >
                                            <Stack spacing={0.75} sx={{ height: "100%" }}>
                                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                    <Typography variant="caption" sx={{ fontWeight: 900 }}>
                                                        {dayNum}
                                                    </Typography>
                                                    {loading ? <CircularProgress size={14} /> : null}
                                                </Stack>

                                                <Stack spacing={0.5} sx={{ flex: 1 }}>
                                                    {dayBookings.length === 0 ? (
                                                        <Typography variant="caption" color="text.secondary">
                                                            —
                                                        </Typography>
                                                    ) : (
                                                        dayBookings.map((b) => {
                                                            const label = `#${b.id}`;
                                                            return (
                                                                <Box
                                                                    key={b.id}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onClick={() => navigate(`/${locale || "el"}/admin/bookings/${b.id}`)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter") navigate(`/${locale || "el"}/admin/bookings/${b.id}`);
                                                                    }}
                                                                    sx={{
                                                                        cursor: "pointer",
                                                                        borderRadius: 2,
                                                                        border: "1px solid",
                                                                        borderColor: "divider",
                                                                        p: 0.75,
                                                                        "&:hover": { backgroundColor: "action.hover" },
                                                                    }}
                                                                >
                                                                    <Stack spacing={0.5}>
                                                                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                                                            <Typography variant="caption" sx={{ fontWeight: 900 }}>
                                                                                {label}
                                                                            </Typography>
                                                                            <BookingStatusChip status={b.status ?? null} size="small" variant="outlined" />
                                                                        </Stack>
                                                                        <Typography variant="caption" color="text.secondary" noWrap title={b.property_title || ""}>
                                                                            {safeStr(b.property_title, b.property_id ? `Property #${b.property_id}` : "—")}
                                                                        </Typography>
                                                                    </Stack>
                                                                </Box>
                                                            );
                                                        })
                                                    )}

                                                    {hasMore ? (
                                                        <Typography variant="caption" color="text.secondary">
                                                            More…
                                                        </Typography>
                                                    ) : null}
                                                </Stack>
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Paper>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                        Rendering shows bookings overlapping each day (checkout is treated as exclusive).
                    </Typography>
                </Box>
            </Paper>
        </Stack>
    );
}
